import React, { useCallback, useContext, useMemo, useState } from "react";
import { Box } from "@mui/material";
import "./styles/NewActivity.scss";
import Breadcrumb from "../../../components/Breadcrumb/Breadcrumb";
import { Link, useParams } from "react-router-dom";
import AppContext from "../../../context/appContext";
import FilterProjectsForActivityCreation from "./FilterProject/FilterProject";
import FilterProjectResult from "../FilterProjectResult/FilterProjectResult";
import ActivitiesContext from "../../../context/activitiesContext";
import NoActivityAttachments from "../../../components/NoActivityAttachments/NoActivityAttachments";
import ContentActivityFooter from "./ContentActivityFooter/ContentActivityFooter";
import { getProjectAttachmentsForActivities } from "../../../services/api/activities/activities.api";
import { getChapterOrEpisodeType } from "./newActivity.utils";
import { useCompiledAttachments } from "../../../hooks/useCompiledAttachments";
import NewActivityModals from "./NewActivityModals/NewActivityModals";
import { getContentTypeLabel } from "../../../shared/constants/contentType";

const NewActivity = () => {
  const { slug } = useParams();
  const [currentProject, setCurrentProject] = useState(null);
  const [showCurrentProjectAttachments, setShowCurrentProjectAttachments] =
    useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [activeSelection, setActiveSelection] = useState(null);
  const [clearSelectionsTrigger, setClearSelectionsTrigger] = useState(0);

  const appContext = useContext(AppContext);
  const activitiesContext = useContext(ActivitiesContext);

  const {
    stateFiles,
    compile,
    clearCompiled,
    toggleActiveCheckBox,
    toggleActiveVersionCheckBox,
    clearActive,
  } = useCompiledAttachments({ groupSupplementaryMaterial: true });

  // remakeSlugType is a pure lookup table, so memoize on the slug to avoid
  // rebuilding/re-reading it on every render (it is used several times below).
  const slugType = useMemo(
    () => activitiesContext.remakeSlugType(slug),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slug]
  );

  const getAttachments = useCallback(
    (item) => {
      clearActive();
      appContext.setShowGlassLoading(true);

      getProjectAttachmentsForActivities(item.id).then((attachment) => {
        const project = { ...attachment.data, ...item };
        const projectFiles = attachment.data?.attachments ?? [];

        setCurrentProject(project);
        appContext.setShowGlassLoading(false);

        compile(
          projectFiles,
          getChapterOrEpisodeType(
            project.contentType ?? getContentTypeLabel(project.type)
          ),
          project.type
        );
      });
    },
    [clearActive, appContext, compile]
  );

  const handleChangeProjects = useCallback(
    (item) => getAttachments(item),
    [getAttachments]
  );

  const showAttachments = useCallback(
    (bool) => setShowCurrentProjectAttachments(bool),
    []
  );

  const clearFiles = useCallback(() => {
    setCurrentProject(null);
    clearCompiled();
    setActiveSelection(null);
    clearActive();
  }, [clearActive, clearCompiled]);

  const openCreateActivityBox = useCallback(
    () => appContext.setCurrentStepBox("createActivity"),
    [appContext]
  );

  const handleContentSelectionChange = useCallback(
    (hasSelection, isValid, step, selectedCount) =>
      setActiveSelection((prev) => {
        if (hasSelection) {
          return { source: "content", isValid, step, selectedCount };
        }
        return prev?.source === "content" ? null : prev;
      }),
    []
  );

  const handleNoActivitySelectionChange = useCallback(
    (info) =>
      setActiveSelection((prev) => {
        if (info) {
          return {
            source: "noActivity",
            isValid: info.isValid,
            step: `createProjectActivity_${info.index}`,
            selectedCount: info.selectedCount,
          };
        }
        return prev?.source === "noActivity" ? null : prev;
      }),
    []
  );

  const handleCreateActivity = useCallback(
    () => appContext.setCurrentStepBox(activeSelection.step),
    [appContext, activeSelection]
  );

  const handleCancel = useCallback(
    () => setClearSelectionsTrigger((t) => t + 1),
    []
  );

  const handleActivityCreated = useCallback(() => {
    setActiveSelection(null);
    setClearSelectionsTrigger((t) => t + 1);
  }, []);

  const isAnyModalOpen =
    !!showAttachmentModal ||
    (!!appContext.current.step && appContext.current.step !== "closed");

  const headerTitle = `Buscar ${
    slugType.type.toLowerCase() === "pesquisa" ? "pesquisa" : "projeto"
  }`;

  return (
    <Box id="NewActivity">
      <Breadcrumb>
        <Box className="newActivityHeader">
          <h2>Nova Atividade</h2>
          <Box component="span">{slugType.label}</Box>
        </Box>

        <Link to="/atividades" className="goBack">
          <Box component="span">{`« Voltar`}</Box>
          <Box component="span">{` para a lista de atividades`}</Box>
        </Link>
      </Breadcrumb>

      <Box id="SearchArea">
        <Box className="searchContainer">
          <Box component="span" className="searchTitle">
            {headerTitle}
          </Box>

          <Box className="formArea">
            <FilterProjectsForActivityCreation
              openBox={openCreateActivityBox}
              getCurrentItem={handleChangeProjects}
              currentId={currentProject?.id}
              showAttachments={showAttachments}
              clearFiles={clearFiles}
            />
          </Box>

          {currentProject?.type !== "Research" && (
            <FilterProjectResult
              currentProject={currentProject}
              files={stateFiles}
              showList={showCurrentProjectAttachments}
              onSelectionChange={handleContentSelectionChange}
              hasGlobalSelection={activeSelection !== null}
              clearSelectionsTrigger={clearSelectionsTrigger}
              clearActive={clearActive}
              toggleActiveCheckBox={toggleActiveCheckBox}
              toggleActiveVersionCheckBox={toggleActiveVersionCheckBox}
              isDisabled={
                activeSelection !== null && activeSelection.source !== "content"
              }
            />
          )}
        </Box>
      </Box>

      {!showCurrentProjectAttachments && (
        <Box className="noActivityAttachments">
          <NoActivityAttachments
            slug={slug}
            onSelectionChange={handleNoActivitySelectionChange}
            hasGlobalSelection={activeSelection !== null}
            clearSelectionsTrigger={clearSelectionsTrigger}
            isDisabled={
              activeSelection !== null &&
              activeSelection.source !== "noActivity"
            }
            onActivityCreated={handleActivityCreated}
          />
        </Box>
      )}

      {activeSelection !== null && !isAnyModalOpen && (
        <ContentActivityFooter
          selectedCount={activeSelection.selectedCount}
          isValid={activeSelection.isValid}
          onCreateActivity={handleCreateActivity}
          onCancel={handleCancel}
          floating
        />
      )}

      <NewActivityModals
        showAttachmentModal={showAttachmentModal}
        setShowAttachmentModal={setShowAttachmentModal}
        currentProject={currentProject}
        type={slugType.label}
        getAttachments={getAttachments}
        openProjectActivityModal={
          appContext.current.step === "createProjectActivity"
        }
        openResearchActivityModal={
          appContext.current.step === "createResearchActivity"
        }
        files={stateFiles}
        toggleActiveCheckBox={toggleActiveCheckBox}
        toggleActiveVersionCheckBox={toggleActiveVersionCheckBox}
        clearActive={clearActive}
        onActivityCreated={handleActivityCreated}
      />
    </Box>
  );
};

export default NewActivity;
